import { Box, Text, Checkbox, Loader, Button, ActionIcon, Tooltip } from '@mantine/core';
import { IconRefresh, IconAlertTriangle, IconTags } from '@tabler/icons-react';
import { formatPrice } from '../../utils/formatPrice';

function parseConfig(cfg) {
  if (!cfg) return {};
  if (typeof cfg === 'string') {
    try { return JSON.parse(cfg); } catch { return {}; }
  }
  return cfg;
}

function offerLabel(o, t) {
  const cfg = parseConfig(o.config);
  switch (o.rule_type) {
    case 'percentage':
      return `${t('takeOrderOfferPercent', 'Percentage discount')} — ${Number(o.value) || 0}%`;
    case 'fixed':
      return `${t('takeOrderOfferFixed', 'Fixed discount')} — ${formatPrice(o.value)}`;
    case 'min_order_discount':
      return `${Number(o.value) || 0}% ${t('takeOrderOfferFrom', 'from')} ${formatPrice(cfg.min_amount)}`;
    case 'free_shipping':
      return t('takeOrderOfferFreeShipping', 'Free shipping');
    case 'free_nth_order':
    case 'buy_x_pay_y': {
      const fi = o.free_items && o.free_items[0];
      return fi ? t(fi.reasonKey, fi.reasonKey, fi.reasonParams || {}) : o.rule_type;
    }
    default:
      return o.rule_type;
  }
}

function offerAmount(o, t) {
  if (o.rule_type === 'free_shipping') return t('takeOrderFree', 'free');
  if (o.rule_type === 'free_nth_order' || o.rule_type === 'buy_x_pay_y') return t('takeOrderFree', 'free');
  if (o.rule_type === 'percentage') return `−${Number(o.value) || 0}%`;
  return `−${formatPrice(o.discount)}`;
}

export default function OffersPanel({
  loading,
  error,
  result,
  selectedOfferIds,
  onToggleOffer,
  applyVolumeDiscount,
  onToggleVolume,
  onRefresh,
  t,
}) {
  if (loading && !result) {
    return (
      <Box className="take-order-offers" aria-live="polite" role="status">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">{t('takeOrderOffersLoading', 'Checking offers…')}</Text>
      </Box>
    );
  }

  if (error && !result) {
    return (
      <Box className="take-order-offers" role="alert">
        <IconAlertTriangle size={18} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
        <Text size="sm" c="dimmed">{t('takeOrderOffersError', 'Could not check offers.')}</Text>
        <Button variant="subtle" size="xs" onClick={onRefresh}>
          {t('retry', 'Retry')}
        </Button>
      </Box>
    );
  }

  if (!result) return null;

  const offers = result.offers || [];
  const hasVolume = Number(result.volumeDiscount) > 0;
  const selected = new Set(selectedOfferIds == null ? offers.map(o => String(o.id)) : selectedOfferIds.map(String));
  const selectedFreeKeys = new Set(
    offers
      .filter(o => selected.has(String(o.id)))
      .flatMap(o => (o.free_items || []).map(f => `${f.variant_id}|${f.reasonKey}`))
  );
  const freeItems = (result.freeItems || []).filter(fi => selectedFreeKeys.has(`${fi.variant_id}|${fi.reasonKey}`));

  if (offers.length === 0 && !hasVolume) {
    return (
      <Box className="take-order-offers take-order-offers--empty">
        <Text size="sm" c="dimmed">{t('takeOrderOffersNone', 'No active offers apply to these items.')}</Text>
      </Box>
    );
  }

  return (
    <Box className="take-order-offers" aria-live="polite">
      <Box className="take-order-offers__head">
        <Text size="sm" fw={600}>
          <IconTags size={16} style={{ verticalAlign: '-3px', marginRight: 6 }} aria-hidden="true" />
          {t('takeOrderOffers', 'Auto-applied offers')}
        </Text>
        <Tooltip label={t('takeOrderOffersRefresh', 'Re-check offers')}>
          <ActionIcon
            variant="subtle"
            size="sm"
            aria-label={t('takeOrderOffersRefresh', 'Re-check offers')}
            onClick={onRefresh}
            disabled={loading}
          >
            {loading ? <Loader size={14} /> : <IconRefresh size={14} />}
          </ActionIcon>
        </Tooltip>
      </Box>

      {offers.map(o => (
        <div className="take-order-offer-row" key={o.id}>
          <Checkbox
            label={offerLabel(o, t)}
            checked={selected.has(String(o.id))}
            onChange={() => onToggleOffer(String(o.id))}
            size="xs"
          />
          <span className={selected.has(String(o.id)) ? 'discount-value' : ''}>{offerAmount(o, t)}</span>
        </div>
      ))}

      {hasVolume && (
        <div className="take-order-offer-row">
          <Checkbox
            label={`${t('takeOrderVolumeDiscount', 'Monthly volume discount')} (${Number(result.volumeDiscountPercentage) || 0}%)`}
            checked={applyVolumeDiscount !== false}
            onChange={onToggleVolume}
            size="xs"
          />
          <span className={applyVolumeDiscount !== false ? 'discount-value' : ''}>−{formatPrice(result.volumeDiscount)}</span>
        </div>
      )}

      {freeItems.length > 0 && (
        <Box mt={6}>
          <Text size="xs" c="dimmed">
            {t('takeOrderOffersFreeItems', 'Includes free items:')}
          </Text>
          {freeItems.map((fi, idx) => (
            <Text size="xs" key={idx} className="take-order-offer-free-item">
              {fi.quantity} × {fi.name} <span className="discount-value">{t('takeOrderFree', 'free')}</span>
            </Text>
          ))}
        </Box>
      )}
    </Box>
  );
}
